import React, { useEffect, useRef } from "react";
import { SFStack, SFGridContainer, SFGridItem } from "../grid";
import SFResponsiveGrid from "../grid/responsiveGrid";
import SFSkeletonGrid from "../skeleton/grid";
import SFSpinner from "../spinner";
import getStyled from "../../hooks/getStyled";

const { styled } = getStyled();

const CardsGridContainer = styled(SFStack)(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
}));

const CardContainer = ({
  triggerLoad,
  card,
  rowData,
  rowCount,
  loadingRows,
  cardVariant,
  cardColumns,
  marginTop,
}) => {
  const loaderRef = useRef();

  const LoaderContainer = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(1),
  }));

  const handleObserver = (entries) => {
    // Gets first element that is observed
    const target = entries[0];
    if (
      // Checks if element is in viewport
      target.isIntersecting
    ) {
      // Scrolls grid to last index
      triggerLoad(rowData?.length);
    }
  };

  useEffect(() => {
    const option = {
      // Makes intersection viewport bigger
      rootMargin: "20px",
    };

    const observer = new IntersectionObserver(handleObserver, option);
    if (loaderRef.current) {
      const element = loaderRef.current;
      observer.observe(element);
      return () => {
        observer.unobserve(element);
      };
    }
  }, [handleObserver, loaderRef]);

  //first we check if loadingRows is true, if it is we show the skeleton grid
  //everytime grid requests more data, we set the loadingRows to true, and when the data is fetched we set it to false
  if (loadingRows) {
    return (
      <CardsGridContainer marginTop={marginTop}>
        {cardColumns ? (
          <SFSkeletonGrid {...cardColumns}></SFSkeletonGrid>
        ) : (
          <SFResponsiveGrid variant={cardVariant} skeleton />
        )}
        <SFResponsiveGrid variant={cardVariant} skeleton />;
      </CardsGridContainer>
    );
  }

  //if rowData is empty, we return null
  //there's a case where grid doesn't request rows
  //when a filter is applied and then we make some changes to the filtered rows that doesn't match with the existing filter and refresh the page
  //the grid won't request new rows, so we need to check if rowData is empty (which is the initial status of the grid)
  if (!rowData) {
    return null;
  }

  return (
    <CardsGridContainer marginTop={marginTop}>
      {cardColumns ? (
        <SFGridContainer columns={cardColumns?.columns}>
          {rowData.map((i, index) => (
            <SFGridItem key={index} {...cardColumns}>
              {card(i)}
            </SFGridItem>
          ))}
        </SFGridContainer>
      ) : (
        <SFResponsiveGrid variant={cardVariant}>
          {rowData.map((data, index) => (
            <React.Fragment key={index}>{card(data)}</React.Fragment>
          ))}
        </SFResponsiveGrid>
      )}
      <LoaderContainer ref={loaderRef}>
        {rowData.length < rowCount && <SFSpinner size="large" />}
      </LoaderContainer>
    </CardsGridContainer>
  );
};

CardContainer.defaultProps = {
  cardVariant: "card",
};

export default CardContainer;
